'use client';

import { forwardRef, useCallback, type ReactNode } from 'react';

import { cn } from '../utils/cn';

import { ComposerAttachmentStack } from './composer/composer-attachment-stack';
import { ComposerRow } from './composer/composer-row';
import { ComposerSendButton } from './composer/composer-send-button';
import { ComposerShell } from './composer/composer-shell';
import { useTypewriterPlaceholder } from '../lib/use-typewriter-placeholder';

import { AttachmentUploadButton } from './attachment-upload-button';
import type { StagedAttachment } from './composer/staged-attachment';

interface LandingComposerProps {
	value: string;
	onChange: (value: string) => void;
	onSubmit: () => void;
	placeholderSuggestions: readonly string[];
	/** While true: textarea disabled, ComposerShell shows SpinnerBorder, typewriter paused. */
	isSubmitting?: boolean;
	/** Disable interaction without showing the submitting treatment. */
	disabled?: boolean;
	autoFocus?: boolean;
	/** textarea name — kept distinct from DOM `name` autofill heuristics (hero uses 'search-domain'). */
	name?: string;
	maxLength?: number;
	className?: string;
	/** Optional attachment chips — rendered as the in-app tilted stack in a footer row. */
	stagedAttachments?: StagedAttachment[];
	onRemoveAttachment?: (id: string) => void;
	/**
	 * When set, renders the in-app "+" attachment button in the footer toolbar
	 * (attachment-only — no formatting tools), so files can be added by click as
	 * well as drag-drop. Receives the picked files.
	 */
	onAddFiles?: (files: FileList | File[]) => void;
	/**
	 * Compact (shorter) input height. The hero opts in because it also shows the
	 * attachment footer, so the textarea itself should stay short; the standalone
	 * CTA composer leaves this off and renders a taller 2-line input.
	 */
	compact?: boolean;
	/**
	 * Static placeholder override. When set, it replaces the typewriter entirely
	 * (the hero pins "Type in what you're working on…" instead of cycling
	 * suggestions). Leave unset to keep the animated typewriter placeholder.
	 */
	placeholder?: string;
	/**
	 * Footer-left slot that stands in for the "+" attachments button. When set
	 * (and no files are staged yet) it replaces the "+" in the toolbar — the hero
	 * uses it for its "or just drop your pile" affordance while the input is empty.
	 * Clear it (pass undefined) to restore the normal "+" button.
	 */
	dropHint?: ReactNode;
}

export const LandingComposer = forwardRef<HTMLTextAreaElement, LandingComposerProps>(
	(
		{
			value,
			onChange,
			onSubmit,
			placeholderSuggestions,
			isSubmitting = false,
			disabled = false,
			autoFocus = false,
			name,
			maxLength,
			className,
			stagedAttachments = [],
			onRemoveAttachment,
			onAddFiles,
			compact = false,
			placeholder: placeholderOverride,
			dropHint,
		},
		ref,
	) => {
		const typewriterPlaceholder = useTypewriterPlaceholder({
			suggestions: placeholderSuggestions,
			enabled: !isSubmitting && !disabled && !placeholderOverride && value.length === 0,
		});

		const placeholder = isSubmitting
			? 'Thinking...'
			: placeholderOverride
				? placeholderOverride
				: value.length > 0
					? 'What are you working on?'
					: typewriterPlaceholder;

		const handleKeyDown = useCallback(
			(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
				if (e.key === 'Enter' && !e.shiftKey) {
					e.preventDefault();
					onSubmit();
				}
			},
			[onSubmit],
		);

		// Sendable with text OR a staged attachment (the hero supports file-only
		// submits). The standalone CTA passes no attachments, so it stays
		// text-required.
		const canSend =
			(value.trim().length > 0 || stagedAttachments.length > 0) &&
			!isSubmitting &&
			!disabled;
		const hasAttachments = stagedAttachments.length > 0 && !!onRemoveAttachment;
		// The footer (toolbar) shows whenever there's a "+" button, a dropHint
		// standing in for it, or any staged attachment to display.
		const hasFooter = !!onAddFiles || hasAttachments || !!dropHint;

		return (
			<div className={cn('relative w-full', className)}>
				<ComposerShell pending={isSubmitting} translucent>
					<ComposerRow variant="main">
						<textarea
							ref={ref}
							name={name}
							value={value}
							onChange={(e) => onChange(e.target.value)}
							onKeyDown={handleKeyDown}
							placeholder={placeholder}
							maxLength={maxLength}
							rows={2}
							autoFocus={autoFocus}
							autoComplete="one-time-code"
							autoCorrect="off"
							autoCapitalize="off"
							spellCheck={false}
							inputMode="text"
							disabled={isSubmitting || disabled}
							data-lpignore="true"
							data-1p-ignore
							data-form-type="other"
							className={cn(
								'min-w-0 flex-1 resize-none bg-transparent text-[16px] leading-relaxed text-neutral placeholder:text-neutral-weak focus:outline-none disabled:opacity-60',
								// 2.5em ≈ 1.5 lines (hero, has the attachment footer); 3.25em
								// ≈ 2 lines (standalone CTA).
								compact ? 'md:!h-[2.5em]' : 'md:!h-[3.25em]',
							)}
						/>
						{/* No footer → the send sits in this row; reserve its footprint
						    so the textarea never runs under it (mirrors chat-composer). */}
						{!hasFooter && (
							<div aria-hidden className="pointer-events-none size-8 shrink-0 opacity-0" />
						)}
					</ComposerRow>
					{hasFooter && (
						<ComposerRow variant="footer">
							<div className="flex min-w-0 flex-1 items-center gap-[5px]">
								{/* Attachment stack sits LEFT of the "+" button, matching the
								    in-app composer toolbar. */}
								{hasAttachments && onRemoveAttachment && (
									<>
										<ComposerAttachmentStack
											attachments={stagedAttachments}
											onRemove={onRemoveAttachment}
										/>
										{(dropHint || onAddFiles) && (
											<span
												className="h-[16px] w-px shrink-0 bg-border-muted"
												aria-hidden
											/>
										)}
									</>
								)}
								{/* The dropHint (when provided) IS the attach control across all
								    states — it stays mounted so the empty→engaged morph animates;
								    otherwise fall back to the standard "+" button. */}
								{dropHint ??
									(onAddFiles && (
										<AttachmentUploadButton
											onFilesSelected={onAddFiles}
											disabled={isSubmitting || disabled}
										/>
									))}
							</div>
							{/* Reserve the send's footprint at the footer's right edge. */}
							<div aria-hidden className="pointer-events-none ml-[8px] size-8 shrink-0 opacity-0" />
						</ComposerRow>
					)}
					{/*
						One send control, pinned to the card's bottom-right (ComposerShell
						is `relative`). It rides the main row's 16px padding when there's no
						footer and drops into the footer's 8px padding once a file is staged,
						so it always aligns with the attachment row instead of floating up
						beside the textarea — matching the in-app composer.
					*/}
					<div
						className={cn(
							'absolute z-10',
							hasFooter ? 'bottom-[8px] right-[8px]' : 'bottom-[16px] right-[16px]',
						)}
					>
						<ComposerSendButton mode="plain" onSend={onSubmit} disabled={!canSend} />
					</div>
				</ComposerShell>
			</div>
		);
	},
);

LandingComposer.displayName = 'LandingComposer';
